import {
  ConversationEventFragment,
  ConversationEventType,
} from '@shared/generated/graphql';
import {
  ConversationEvent,
  ConversationEventData,
  isQuietLineIntentEventType,
} from '@shared/types/conversation';
import { assertUnreachable } from 'clerk_common/enum';
import { isNil } from 'lodash';

export const conversationEventFromFragment = (
  fragment: ConversationEventFragment
): ConversationEvent => {
  return {
    id: fragment.id,
    createdAt: new Date(fragment.createdAt),
    name: fragment.name,
    data: conversationEventDataFromFragment(fragment.data),
  };
};

function conversationEventDataFromFragment(
  data: ConversationEventFragment['data']
): ConversationEventData | undefined {
  const variantName = data?.__typename;
  if (isNil(data) || isNil(variantName)) return undefined;
  switch (variantName) {
    case 'CallFailedData':
      return {
        ...data,
        eventDataType: ConversationEventType.CALL_FAILED,
      };
    case 'CallEndedData':
      return {
        ...data,
        eventDataType: ConversationEventType.CALL_ENDED,
      };
    case 'ChatViewContextUpdatedData':
      return {
        context: data.context,
        eventDataType: ConversationEventType.CHAT_VIEW_CONTEXT_UPDATED,
      };
    case 'CallTransferredData':
      return {
        ...data,
        eventDataType: ConversationEventType.CALL_TRANSFERRED,
      };
    case 'CallTransferAcceptedData':
      return {
        ...data,
        eventDataType: ConversationEventType.CALL_TRANSFER_ACCEPTED,
      };
    case 'AsyncTransferData':
      return {
        ...data,
        eventDataType: ConversationEventType.ASYNC_TRANSFER,
      };
    case 'ReturnedToTransferredCallData':
      return {
        ...data,
        eventDataType: ConversationEventType.RETURNED_TO_TRANSFERRED_CALL,
      };
    case 'ConferenceJoinedData':
      return {
        ...data,
        eventDataType: ConversationEventType.CONFERENCE_JOINED,
      };
    case 'CallRecordingStartedData':
      return {
        eventDataType: ConversationEventType.CALL_RECORDING_STARTED,
      };
    case 'CallRecordingReceivedData':
      return {
        ...data,
        eventDataType: ConversationEventType.CALL_RECORDING_RECEIVED,
        recordingStartTime: new Date(data.recordingStartTime),
        recordingEndTime: new Date(data.recordingEndTime),
      };
    case 'WatcherClosedData':
      return {
        eventDataType: ConversationEventType.CONVERSATION_CLOSED_BY_WATCHER,
      };
    case 'QuietLineIntentData':
      if (!isQuietLineIntentEventType(data.eventDataType)) {
        console.warn(
          `Unexpected quiet line intent event data type: ${data.eventDataType}`
        );
        return { eventDataType: ConversationEventType.UNKNOWN };
      }
      return { eventDataType: data.eventDataType };
    case 'UnknownConversationEventData':
      return {
        ...data,
        eventDataType: ConversationEventType.UNKNOWN,
      };
    default:
      return assertUnreachable(variantName);
  }
}
